// Project → Shipping tab (Stephanie 2026-10-12: "in commonsku there was a
// shipping tab"). The quote's item-by-item Shipping plan — where each item
// ships from the supplier and the decorator — lives here instead of on the
// quote page. Same component (OrderShippingTab), same data; only its home
// moved. Since 2026-10-27 it is open before the client approves; Create
// purchase orders still waits for approval.
//
// The `/projects/:id/shipping` URL used to redirect to Overview (the old
// Tracking tab, removed 2026-07-29); it is a real tab again.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Link, useOutletContext } from "react-router-dom";
import { UNNAMED_ARTWORK_LOCATION, approvedQuoteAwaitingCommit, linesMissingLogo } from "@merchd/shared";
import { api } from "@/lib/api";
import { OrderShippingTab, type ShippingLineArt } from "@/components/orders/OrderShippingTab";
import { docLineImageUrl } from "@/components/documents/types";
import { shouldShowNextAction } from "@/components/orders/nextAction";
import { CreatePurchaseOrders, type CreatePoLine, type CreatePoOrder } from "@/components/orders/CreatePurchaseOrders";
import { useAuth } from "@/auth/AuthProvider";
import { orderStatusLabel } from "@/lib/orders";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface QuoteRow {
  id: string;
  orderNumber: number;
  statusStage: string;
  statusState?: string;
}

interface QuoteDetail {
  order: CreatePoOrder & {
    statusStage: string;
    statusState?: string;
    soApprovedAt: string | null;
    upfrontPaymentStatus: string | null;
    requiresUpfrontPayment: boolean | null;
    /** Hide the in-hands date from the supplier's POs; null = not switched on yet. */
    poHideInHands?: boolean | null;
  };
  client: { clientId: string } | null;
  lines: CreatePoLine[];
}

export function ProjectShippingTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const projectId = data.project.id;
  const { user } = useAuth();
  // Creating POs is the sales→production handoff: admin / production rep only
  // (the POST /orders/:id/pos roles), same as the quote page.
  const canCreatePo = !!user && (user.roles.includes("admin") || user.roles.includes("production_rep"));

  // The project's quote (one per project): the same post-estimate stages the
  // Quote tab lists, cancelled left out.
  const list = useQuery({
    queryKey: ["project", projectId, "orders", "sales-orders"] as const,
    queryFn: () =>
      api<{ rows: QuoteRow[] }>(
        `/orders?project_id=${projectId}` +
          `&stage=quote&stage=committed&stage=in_production&stage=shipped&stage=invoiced&stage=closed&stage=cancelled` +
          `&limit=100`,
      ),
  });
  // An opportunity can hold several live orders (CEO 2026-10-15) — plan each
  // one's shipping; pick which when there's more than one.
  // A client-approved quote can still be at stage "quote" (approval stamps
  // so_approved_at, it doesn't move the stage), so quote-stage rows count too;
  // the plan is open for every quote (Stephanie 2026-10-27).
  // Quotes already past "quote" are listed first.
  const live = (list.data?.rows ?? [])
    .filter((r) => r.statusStage !== "cancelled")
    .sort((a, b) => Number(a.statusStage === "quote") - Number(b.statusStage === "quote"));
  const [pickedId, setPickedId] = useState<string | null>(null);
  const quote = live.find((r) => r.id === pickedId) ?? live[0];

  if (list.isLoading) {
    return <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading shipping" />;
  }
  return (
    <>
      {live.length > 1 && quote && (
        <label className="mb-4 flex flex-wrap items-center gap-3 text-[15px] text-ink">
          <span className="font-semibold">Shipping for</span>
          <select
            value={quote.id}
            onChange={(e) => setPickedId(e.target.value)}
            className="h-10 rounded-md border border-rule bg-white px-3 text-[15px]"
          >
            {live.map((r) => (
              <option key={r.id} value={r.id}>
                Quote #{r.orderNumber} · {orderStatusLabel(r.statusStage, r.statusState ?? "")}
              </option>
            ))}
          </select>
        </label>
      )}
      <ShippingForQuote key={quote?.id ?? "none"} projectId={projectId} quote={quote} listError={list.isError} canCreatePo={canCreatePo} />
    </>
  );
}

function ShippingForQuote({
  projectId,
  quote,
  listError,
  canCreatePo,
}: {
  projectId: string;
  quote: QuoteRow | undefined;
  listError: boolean;
  canCreatePo: boolean;
}) {
  const detail = useQuery({
    queryKey: ["project", projectId, "shipping", quote?.id],
    enabled: !!quote,
    queryFn: () => api<QuoteDetail>(`/orders/${quote!.id}`),
  });

  if (quote && detail.isLoading) {
    return <div className="h-24 animate-pulse rounded-md bg-white" aria-label="Loading shipping" />;
  }
  if (listError || detail.isError) {
    return (
      <p role="alert" className="rounded-md border border-rule bg-white px-5 py-6 text-sm text-risk">
        We couldn't load this project's shipping. Nothing has changed — refresh to try again.
      </p>
    );
  }
  if (!quote || !detail.data) {
    return (
      <div className="rounded-md border border-rule bg-white px-5 py-12 text-center text-sm text-muted">
        Shipping is planned on the quote's items. This project doesn't have a quote yet —{" "}
        <Link to={`/projects/${projectId}/quotes`} className="font-semibold text-ink hover:text-muted">
          open the Quote tab →
        </Link>
      </div>
    );
  }

  const order = detail.data.order;
  const quoteHref = `/projects/${projectId}/quotes/${order.id}`;

  // No shipping until every item has its logo — unless the quote is ticked
  // "Blank order" (Stephanie 2026-10-14). Same per-location rule as Create
  // POs; only applies once the Blank order box exists (blankOrder not null).
  const missingLogos = linesMissingLogo(
    detail.data.lines
      .filter((l) => l.serviceType === "product")
      .map((l) => ({
        id: l.id,
        itemName: l.productSnapshot?.name ?? l.description ?? "Item",
        decorationPath: l.decorationPath,
        decorations: (l.decorations ?? [])
          // The "+ Add cost" holder is not an imprint location — no artwork.
          .filter((d) => d.kind !== "item_costs")
          .map((d) => ({
          location: d.location,
          hasLogoFile: d.logoFileId != null,
          hasLogoName: !!d.logoName?.trim(),
          hasProofType: d.proofType != null,
        })),
      })),
    order.blankOrder,
  );
  if (missingLogos.length > 0) {
    return (
      <div className="rounded-md border border-rule bg-white px-5 py-6 text-sm text-ink">
        <p className="font-semibold">Add the logo first</p>
        <p className="mt-1 text-muted">
          Every item needs its logo before shipping can be planned. If this order has no logo, tick{" "}
          <span className="font-semibold text-ink">Blank order</span> on the quote.
        </p>
        <ul className="mt-3 space-y-1">
          {missingLogos.map((m, i) => (
            <li key={`${m.lineItemId}-${m.location}-${i}`}>
              <Link to={`${quoteHref}#order-line-${m.lineItemId}`} className="font-semibold text-ink hover:text-muted">
                {m.itemName}
                {m.location && m.location !== UNNAMED_ARTWORK_LOCATION ? ` — ${m.location}` : ""}
              </Link>
              <span className="text-muted">
                {m.location === UNNAMED_ARTWORK_LOCATION ? " · no logo added yet" : ` · needs ${m.missing.join(", ")}`}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Each item's picture and logos for the plan (Stephanie 2026-10-27).
  const lineArt = new Map<string, ShippingLineArt>(
    detail.data.lines.map((l) => [
      l.id,
      {
        imageUrl: docLineImageUrl({ productSnapshot: l.productSnapshot } as Parameters<typeof docLineImageUrl>[0]),
        logos: (l.decorations ?? [])
          .filter((d) => d.kind !== "item_costs" && d.logoFileId)
          .map((d) => ({ fileId: d.logoFileId!, location: d.location && d.location !== UNNAMED_ARTWORK_LOCATION ? d.location : null, name: d.logoName ?? null })),
      },
    ]),
  );

  return (
    <>
    <PoInHandsBox orderId={order.id} projectId={projectId} quoteId={quote!.id} hide={order.poHideInHands ?? null} />
    <OrderShippingTab
      orderId={order.id}
      lineArt={lineArt}
      clientId={detail.data.client?.clientId ?? null}
      // Open for every quote, approved or not (Stephanie 2026-10-27: "make
      // shipping open even if the quote isnt approved"). Create purchase
      // orders below still waits for approval (shouldShowNextAction).
      unlocked
      // The rep's "Mark approved" lives on the quote (#so-approval) — none for
      // an upfront quote, which the client approves by paying.
      approveHref={order.requiresUpfrontPayment === true ? null : `${quoteHref}#so-approval`}
      // Create purchase orders sits right under the plan here (Stephanie
      // 2026-10-13), so the plan's own "Create purchase orders →" pointer is off.
      createPoHref={null}
      // Also an approved quote that never became an order — creating its POs
      // switches it over (Stephanie 2026-10-27).
      renderCreatePo={
        shouldShowNextAction(order.statusStage) || approvedQuoteAwaitingCommit(order)
          ? (plan, selection) => (
              <CreatePurchaseOrders order={order} lines={detail.data!.lines} canCreatePo={canCreatePo} plan={plan} selection={selection} />
            )
          : undefined
      }
    />
    </>
  );
}

/** "Show the in-hands date on POs" (Stephanie 2026-10-27: "add an option to
 *  hide the in hands date from the PO … do it in the shipping tab"). One box
 *  for every PO of this quote; the supplier's PO and email leave the date out
 *  when it's unticked. Greyed out until Jake's database step. */
function PoInHandsBox(props: { orderId: string; projectId: string; quoteId: string; hide: boolean | null }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (hide: boolean) =>
      api<{ hide: boolean }>(`/orders/${props.orderId}/po-hide-in-hands`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hide }),
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["project", props.projectId, "shipping", props.quoteId] });
    },
    onError: () => setError("Couldn't save that. Try again."),
  });
  const ready = props.hide !== null;
  return (
    <div className="mb-4 rounded-md border border-rule bg-white px-5 py-3">
      <label className={`inline-flex items-center gap-2 text-[15px] ${ready ? "text-ink" : "text-muted"}`}>
        <input
          type="checkbox"
          checked={props.hide !== true}
          disabled={!ready || save.isPending}
          onChange={(e) => save.mutate(!e.target.checked)}
          className="h-4 w-4 accent-[#080808]"
        />
        Show the client&rsquo;s in-hands date on POs
      </label>
      <span className="ml-2 text-sm text-muted">
        {ready ? "Untick to keep it off the supplier's PO and email." : "Waiting on a database step from Jake."}
      </span>
      {error && <p role="alert" className="mt-1 text-sm text-risk">{error}</p>}
    </div>
  );
}
